import { inject, injectable } from 'inversify'
import Debug from 'debug';
import { StateMachine, StateChangedListener } from '@/app/shared/state-machine'
import { QuestionStoreModule, QuestionStoreModuleSymbol } from '@/app/question';
import * as States from './states'
import * as Data from './data'
import { PracticeStoreModule, PracticeStoreModuleSymbol } from '@/app/practice/store';
import { LoadQuestionsJob } from '@/app/question/store';
import { QuestionAttemptService, QuestionAttemptServiceSymbol } from '@/app/question-attempt/service';
import { QuestionAttemptQuery, QuestionAttemptQuerySymbol } from '@/app/question-attempt/store';
import { PracticeSessionService, PracticeSessionServiceSymbol } from '@/app/practice-session/service';

const debug = Debug('practice-state-machine')

type State = States.PracticeInitialState | States.PracticeStartingState |
      States.PracticeInProgressState | States.PracticeReviewState |
      States.PracticeSummaryState | States.PracticeErrorState |
      States.PracticeCanceledState

@injectable()
export class PracticeStateMachine {

  private stateMachine: StateMachine<State, States.PracticeStateMachinePhase>
  private adaptivity: States.Adaptivity
  private data: Data.PracticeStateMachineData = new Data.PracticeStateMachineData()

  constructor(
    @inject(PracticeStoreModuleSymbol)
    private practiceStoreModule: PracticeStoreModule,
    @inject(QuestionStoreModuleSymbol)
    private questionStore: QuestionStoreModule,
    @inject(QuestionAttemptServiceSymbol)
    private questionAttemptService: QuestionAttemptService,
    @inject(QuestionAttemptQuerySymbol)
    questionAttemptQuery: QuestionAttemptQuery,
    @inject(PracticeSessionServiceSymbol)
    private practiceSessionService: PracticeSessionService ) {

    this.adaptivity = new States.Adaptivity(questionAttemptQuery)
    this.stateMachine = new StateMachine<State, States.PracticeStateMachinePhase>(
      'practice-state-machine')
    this.stateMachine.addStateChangedListener((phase) => {
      this.data.updatePhase(phase)
    })
  }

  initialize(state: Data.PracticeStateMachineData) {
    this.data = state

    this.gotoInitialState()
  }

  start(options: States.PracticeStartOptions) {
    this.stateMachine.executeAction('start', options)
  }
  
  navigate(questionIndex: number) {
    this.stateMachine.executeAction('navigate', questionIndex)
  }

  navigatePrevious() {
    this.stateMachine.executeAction('navigatePrevious')
  }

  navigateNext() {
    this.stateMachine.executeAction('navigateNext')
  }

  changeAnswer(answer: Data.PracticeQuestionAnswer,
               evaluation: Data.PracticeQuestionEvaluationResult) {

    this.stateMachine.executeAction('changeAnswer', answer, evaluation)
  }

  changeState(state: Data.PracticeQuestionState) {
    this.stateMachine.executeAction('changeState', state)
  }

  evaluateQuestion() {
    this.stateMachine.executeAction('evaluateQuestion')
  }

  showQuestionSolution() {
    this.stateMachine.executeAction('showQuestionSolution')
  }

  resetQuestion() {
    this.stateMachine.executeAction('resetQuestion')
  }

  finish() {
    this.stateMachine.executeAction('finish')
  }

  reset() {
    this.stateMachine.executeAction('reset')
  }

  cancel() {
    this.stateMachine.executeAction('cancel')
  }

  private gotoInitialState() {
    const events = {
      ...this.commonEventHandlers,
      onStartRequested: (options: States.PracticeStartOptions) => this.handleStartRequested(options)
      
    }
    const initialState = new States.PracticeInitialState(this.data)
    this.stateMachine.changeState(initialState, events)
    }

  private handleStartRequested(options: States.PracticeStartOptions) {
    const events = {
      ...this.commonEventHandlers,
      onStarted: () => {
        this.handleStarted()
      }
    }
    const startingState = new States.PracticeStartingState(options,
                                                    new States.LoadPracticeJob(this.practiceStoreModule),
                                                    new LoadQuestionsJob(this.questionStore),
                                                    this.adaptivity,
                                                    this.data,
                                                    this.practiceSessionService)
    this.stateMachine.changeState(startingState, events)
  }

  private handleStarted() {
    const events = {
      ...this.commonEventHandlers,
      onFinished: () => {
        this.gotoFinished()
      }
    }
    const inProgressState = new States.PracticeInProgressState(this.data, this.adaptivity,
                                                        this.questionAttemptService,
                                                        this.practiceSessionService)
    this.stateMachine.changeState(inProgressState, events)
  }

  private handleReview(questionIndex: number) {
    const events = {
      ...this.commonEventHandlers,
      onReviewFinished: () => {
        this.handleReviewFinished()
      }
    }
    const reviewState = new States.PracticeReviewState(questionIndex, this.data)
    this.stateMachine.changeState(reviewState, events)
  }

  private handleReviewFinished() {
    this.gotoFinished()
  }

  private gotoFinished() {
    const events = {
      ...this.commonEventHandlers,
      onReviewRequested: (questionIndex: number) => {
        this.handleReview(questionIndex)
      },
      onReset: () => {
      this.handleReset()
      } 
    }
    const summaryState = new States.PracticeSummaryState(this.data)
    this.stateMachine.changeState(summaryState, events)
  }

  private handleReset() {
    debug('handleReset')
    this.gotoInitialState()
  }

  addStateChangedListener(listener: StateChangedListener<States.PracticeStateMachinePhase>) {
    return this.stateMachine.addStateChangedListener(listener)
  }
  
  removeStateChangedListener(token: number) {
    this.stateMachine.removeStateChangedListener(token)
  }

  private get commonEventHandlers(): States.PracticeStateMachineCommonEvents {
    return {
      onCanceled: () => this.handleCanceled(),
      onError: (err) => this.handleError(err)
    }
  }

  private handleCanceled() {
    const canceledState = new States.PracticeCanceledState()
    this.stateMachine.changeState(canceledState, this.commonEventHandlers)
  }

  private handleError(err: unknown) {
    const events = {
      ...this.commonEventHandlers,
      onReset: () => this.handleReset()
    }
    const errorState = new States.PracticeErrorState(this.data, err)
    this.stateMachine.changeState(errorState, events)
  }

  get error() {
    return this.data.error
  }
}

